import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Table, Drawer, Empty, Button, Message, Tooltip, Pagination } from '@arco-design/web-react'
import { IconCopy, IconLink } from '@arco-design/web-react/icon'
import { useRole } from '@/hooks/useRole'
import { useTaskLogs, type TaskColumnKey } from '@/hooks/useTaskLogs'
import TaskLogsFilters from '@/components/task/TaskLogsFilters'
import {
  renderTimestamp,
  renderUseTime,
  renderTaskStatus,
  Pill,
} from '@/utils/render'
import { drawerWidth } from '@/utils/responsive'
import type { TaskEntry } from '@/api/task'

function prettyTaskData(raw?: TaskEntry['data']) {
  if (raw === undefined || raw === null || raw === '') return ''
  if (typeof raw === 'string') {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2)
    } catch {
      return raw
    }
  }
  try {
    return JSON.stringify(raw, null, 2)
  } catch {
    return String(raw)
  }
}

function copyTaskDataText(raw?: TaskEntry['data']): string {
  if (raw === undefined || raw === null || raw === '') return ''
  if (typeof raw === 'string') return raw
  try {
    return JSON.stringify(raw, null, 2)
  } catch {
    return String(raw)
  }
}

function DetailRow({
  label,
  children,
  onCopy,
  copyLabel,
}: {
  label: string
  children: ReactNode
  onCopy?: () => void
  copyLabel?: string
}) {
  return (
    <div>
      <div className="text-xs text-[var(--console-text-subtle)] mb-0.5">{label}</div>
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1 break-all text-[var(--console-text-body)]">{children}</div>
        {onCopy ? (
          <Button
            type="text"
            size="mini"
            icon={<IconCopy />}
            aria-label={copyLabel}
            onClick={onCopy}
            className="flex-shrink-0"
          />
        ) : null}
      </div>
    </div>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-[13px] font-semibold text-[var(--console-text-h)] mb-3 pb-2 border-b border-[var(--console-border)]">
      {children}
    </h3>
  )
}

export default function TaskPage() {
  const { t } = useTranslation()
  const { isAdmin } = useRole()
  const {
    filters,
    updateFilters,
    resetFilters,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    items,
    total,
    visibleColumns,
    setVisibleColumns,
    platformOptions,
    actionOptions,
  } = useTaskLogs(isAdmin)

  const [detailOpen, setDetailOpen] = useState(false)
  const [detailRow, setDetailRow] = useState<TaskEntry | null>(null)
  const tableAreaRef = useRef<HTMLDivElement>(null)
  const [tableBodyScrollY, setTableBodyScrollY] = useState(400)

  const syncTableLayout = useCallback(() => {
    const area = tableAreaRef.current
    if (!area) return

    const header = area.querySelector('.arco-table-header') as HTMLElement | null
    const theadH = header?.offsetHeight ?? 48
    const bodyH = Math.max(120, area.clientHeight - theadH)

    setTableBodyScrollY((prev) => (prev === bodyH ? prev : bodyH))

    const body = area.querySelector('.arco-table-body') as HTMLElement | null
    if (body) {
      // Arco 仅设置 maxHeight，行数少时表体不会撑满；强制 height 贴到分页条上方
      body.style.height = `${bodyH}px`
      body.style.maxHeight = `${bodyH}px`
      body.style.overflowY = 'auto'
    }
  }, [])

  useLayoutEffect(() => {
    syncTableLayout()
    const area = tableAreaRef.current
    if (!area) return

    const ro = new ResizeObserver(() => syncTableLayout())
    ro.observe(area)
    window.addEventListener('resize', syncTableLayout)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncTableLayout)
    }
  }, [syncTableLayout])

  useLayoutEffect(() => {
    syncTableLayout()
  }, [syncTableLayout, query.isLoading, items.length, visibleColumns.length, filters.scope])

  const hasActiveFilters =
    !!filters.task_id?.trim() ||
    !!filters.platform ||
    !!filters.action ||
    !!filters.status ||
    !!filters.channel_id ||
    !!filters.start_timestamp ||
    !!filters.end_timestamp

  const openDetail = (row: TaskEntry) => {
    setDetailRow(row)
    setDetailOpen(true)
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => Message.success(t('common.copied')))
  }

  const columnsAll = useMemo(
    () => ({
      submit_time: {
        title: t('tt.task.submitTime'),
        dataIndex: 'submit_time',
        width: 170,
        render: (v?: number) => renderTimestamp(v),
      },
      finish_time: {
        title: t('tt.task.finishTime'),
        dataIndex: 'finish_time',
        width: 170,
        render: (v?: number) => renderTimestamp(v),
      },
      duration: {
        title: t('tt.task.duration'),
        dataIndex: 'duration',
        width: 100,
        render: (_: unknown, row?: TaskEntry) =>
          row?.submit_time && row?.finish_time ? (
            renderUseTime(row.finish_time - row.submit_time)
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      platform: {
        title: t('tt.task.platform'),
        dataIndex: 'platform',
        width: 140,
        render: (v?: string) =>
          v ? <Pill variant="neutral">{v}</Pill> : <span className="text-[var(--console-text-subtle)]">-</span>,
      },
      channel_id: {
        title: t('tt.task.channel'),
        dataIndex: 'channel_id',
        width: 90,
        render: (v?: number) =>
          v != null && v > 0 ? (
            <Pill variant="neutral">#{v}</Pill>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      task_id: {
        title: t('tt.task.taskId'),
        dataIndex: 'task_id',
        width: 220,
        render: (v?: string, row?: TaskEntry) =>
          v ? (
            <Tooltip content={t('tt.task.viewDetail')}>
              <button
                type="button"
                className="text-xs text-[var(--brand-primary)] font-mono hover:underline text-left break-all max-w-full"
                onClick={() => row && openDetail(row)}
              >
                {v}
              </button>
            </Tooltip>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      username: {
        title: t('tt.task.user'),
        dataIndex: 'username',
        width: 120,
        ellipsis: true,
        render: (v?: string) =>
          v ? (
            <span className="text-xs text-[var(--console-text-body)]" title={v}>
              {v}
            </span>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      action: {
        title: t('tt.task.action'),
        dataIndex: 'action',
        width: 140,
        render: (v?: string) =>
          v ? <Pill variant="info">{v}</Pill> : <span className="text-[var(--console-text-subtle)]">-</span>,
      },
      status: {
        title: t('tt.task.status'),
        dataIndex: 'status',
        width: 110,
        render: (v?: string) => renderTaskStatus(v),
      },
      progress: {
        title: t('tt.task.progress'),
        dataIndex: 'progress',
        width: 100,
        render: (v?: string) =>
          v ? (
            <span className="font-mono text-xs text-[var(--console-text-body)]">{v}</span>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      result_url: {
        title: t('tt.task.resultUrl'),
        dataIndex: 'result_url',
        width: 120,
        ellipsis: true,
        render: (v?: string) =>
          v ? (
            <Tooltip content={v}>
              <a
                href={v}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[var(--brand-primary)] hover:underline"
                aria-label={t('tt.task.openLink')}
              >
                <IconLink />
                {t('tt.task.openLink')}
              </a>
            </Tooltip>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
      fail_reason: {
        title: t('tt.task.failReason'),
        dataIndex: 'fail_reason',
        ellipsis: true,
        render: (v?: string) =>
          v ? (
            <span className="text-xs text-[var(--console-text-body)] line-clamp-2" title={v}>
              {v}
            </span>
          ) : (
            <span className="text-[var(--console-text-subtle)]">-</span>
          ),
      },
    }),
    [t],
  )

  const columns = visibleColumns
    .map((key) => (columnsAll as Record<TaskColumnKey, unknown>)[key])
    .filter(Boolean) as Array<{ title: string }>

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-8.5rem)] min-h-[480px]">
      <Card className="tt-card !rounded-2xl shrink-0" bordered={false} bodyStyle={{ padding: 16 }}>
        <TaskLogsFilters
          filters={filters}
          onChange={updateFilters}
          onReset={resetFilters}
          onRefresh={() => query.refetch()}
          isAdmin={isAdmin}
          loading={query.isFetching}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          platformOptions={platformOptions}
          actionOptions={actionOptions}
        />
      </Card>

      <Card
        className="tt-card !rounded-2xl flex flex-col flex-1 min-h-0 [&_.arco-card-body]:!flex [&_.arco-card-body]:!flex-col [&_.arco-card-body]:!min-h-0"
        bordered={false}
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          ref={tableAreaRef}
          className="min-h-0 min-w-0 flex-1 overflow-hidden flex flex-col h-full"
          style={{ ['--console-table-body-h' as string]: `${tableBodyScrollY}px` }}
        >
          <Table
            rowKey="id"
            loading={query.isLoading}
            columns={columns as never}
            data={items}
            tableLayoutFixed
            scroll={{ x: 1680, y: tableBodyScrollY }}
            stripe={false}
            borderCell={false}
            pagination={false}
            style={{ height: '100%', minHeight: 0 }}
            className="console-table-fill task-list-table h-full min-h-0 flex-1 [&_.arco-table-container]:max-w-full [&_.arco-table-th]:!py-3 [&_.arco-table-td]:!py-3"
            noDataElement={
              <div className="py-10">
                <Empty
                  description={
                    hasActiveFilters ? t('tt.task.emptyFiltered') : t('tt.task.empty')
                  }
                />
              </div>
            }
          />
        </div>
        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-x-4 gap-y-2 px-4 py-3 border-t border-[var(--console-border)] bg-[var(--console-hover-row)] rounded-b-2xl">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal={(n, range) =>
              n > 0
                ? t('tt.apiKey.pagination', { from: range[0], to: range[1], total: n })
                : t('tt.task.paginationTotal', { total: n })
            }
            sizeCanChange
            pageSizeChangeResetCurrent
            sizeOptions={[10, 20, 50, 100]}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </div>
      </Card>

      <Drawer
        title={t('tt.task.detailTitle')}
        visible={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={drawerWidth(640)}
        unmountOnExit
      >
        {detailRow && (
          <div className="space-y-6 text-sm">
            <div>{renderTaskStatus(detailRow.status)}</div>

            <div>
              <SectionTitle>{t('tt.task.sectionBasic')}</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailRow
                  label={t('tt.task.taskId')}
                  onCopy={
                    detailRow.task_id
                      ? () => handleCopy(detailRow.task_id as string)
                      : undefined
                  }
                  copyLabel={t('复制')}
                >
                  <code className="text-xs font-mono">{detailRow.task_id || '-'}</code>
                </DetailRow>
                {detailRow.username ? (
                  <DetailRow label={t('tt.task.user')}>{detailRow.username}</DetailRow>
                ) : null}
                <DetailRow label={t('tt.task.platform')}>{detailRow.platform || '-'}</DetailRow>
                <DetailRow label={t('tt.task.action')}>{detailRow.action || '-'}</DetailRow>
                <DetailRow label={t('tt.task.channel')}>
                  {detailRow.channel_id != null && detailRow.channel_id > 0
                    ? `#${detailRow.channel_id}`
                    : '-'}
                </DetailRow>
                <DetailRow label={t('tt.task.progress')}>
                  <span className="font-mono">{detailRow.progress || '-'}</span>
                </DetailRow>
              </div>
            </div>

            <div>
              <SectionTitle>{t('tt.task.sectionTime')}</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailRow label={t('tt.task.submitTime')}>
                  {renderTimestamp(detailRow.submit_time)}
                </DetailRow>
                <DetailRow label={t('tt.task.startTime')}>
                  {renderTimestamp(detailRow.start_time)}
                </DetailRow>
                <DetailRow label={t('tt.task.finishTime')}>
                  {renderTimestamp(detailRow.finish_time)}
                </DetailRow>
                {detailRow.submit_time && detailRow.finish_time ? (
                  <DetailRow label={t('tt.task.duration')}>
                    {renderUseTime(detailRow.finish_time - detailRow.submit_time)}
                  </DetailRow>
                ) : null}
              </div>
            </div>

            {(detailRow.result_url || detailRow.fail_reason) && (
              <div>
                <SectionTitle>{t('tt.task.sectionResult')}</SectionTitle>
                <div className="space-y-4">
                  {detailRow.result_url ? (
                    <DetailRow
                      label={t('tt.task.resultUrl')}
                      onCopy={() => handleCopy(detailRow.result_url as string)}
                      copyLabel={t('复制')}
                    >
                      <a
                        href={detailRow.result_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--brand-primary)] break-all hover:underline"
                      >
                        {detailRow.result_url}
                      </a>
                    </DetailRow>
                  ) : null}
                  {detailRow.fail_reason ? (
                    <div>
                      <div className="text-xs text-[var(--console-text-subtle)] mb-1">
                        {t('tt.task.failReason')}
                      </div>
                      <div className="p-3 bg-[var(--semantic-danger-bg,#FEE2E2)] border border-[var(--console-border)] rounded-lg text-sm text-[var(--semantic-danger-text,#991B1B)] whitespace-pre-wrap break-all">
                        {detailRow.fail_reason}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {detailRow.data != null && detailRow.data !== '' && (
              <div>
                <SectionTitle>{t('tt.task.sectionRaw')}</SectionTitle>
                <div className="flex items-center justify-end mb-1">
                  <Button
                    type="text"
                    size="mini"
                    icon={<IconCopy />}
                    aria-label={t('复制')}
                    onClick={() => handleCopy(copyTaskDataText(detailRow.data))}
                  >
                    {t('复制')}
                  </Button>
                </div>
                <pre className="p-3 bg-[var(--console-page-bg)] border border-[var(--console-border)] rounded-lg text-xs font-mono overflow-auto max-h-96 whitespace-pre-wrap break-all">
                  {prettyTaskData(detailRow.data)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}
